/**
 * AttendanceReasonForm — parent-facing structured response for the
 * `attendance-reason` Smart Message format.
 *
 * Renders inline inside SmartMessageBubble (parent variant) when the
 * incoming message uses responseFormat === 'attendance-reason'. Collects:
 *
 *   - excuseType (medical / family / appointment / bereavement / other)
 *   - reason text
 *   - date range booked off (defaults to the triggering streak window)
 *   - supporting document
 *   - typed-name signature + affirmation
 *
 * Submission funnels through `respondToSmartMessage` so the recipient doc
 * advances to `responded`. The structured payload travels on the callable
 * call as `responseMetadata`; the Cloud Function persists it onto the
 * recipient's `response.metadata` (Stage 3 wires the attendance event
 * write-back from there).
 */
import { useMemo, useState } from 'react';
import {
  respondToSmartMessage,
  uploadSmartMessageAttachment,
} from '@/services/messaging/SmartMessageService';

const EXCUSE_CHIPS = [
  { id: 'medical', label: 'Illness / medical' },
  { id: 'family', label: 'Family event' },
  { id: 'appointment', label: 'Appointment' },
  { id: 'bereavement', label: 'Bereavement' },
  { id: 'other', label: 'Other' },
];

function defaultEndDate() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function defaultStartDate(windowDates) {
  if (Array.isArray(windowDates) && windowDates.length > 0) {
    return [...windowDates].sort()[0];
  }
  const d = new Date();
  d.setDate(d.getDate() - 2);
  return d.toISOString().slice(0, 10);
}

export default function AttendanceReasonForm({
  schoolId,
  smartMessageId,
  studentId,
  windowDates = [],
  onResponded,
}) {
  const [excuseType, setExcuseType] = useState('medical');
  const [reason, setReason] = useState('');
  const [startDate, setStartDate] = useState(() => defaultStartDate(windowDates));
  const [endDate, setEndDate] = useState(() => defaultEndDate());
  const [signature, setSignature] = useState('');
  const [affirmed, setAffirmed] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const canConfirm = useMemo(() => {
    return (
      !!reason.trim() &&
      !!signature.trim() &&
      affirmed &&
      !!startDate &&
      !!endDate
    );
  }, [reason, signature, affirmed, startDate, endDate]);

  async function submit(optionId) {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      let documentRef = null;
      if (file && optionId === 'confirm') {
        const uploaded = await uploadSmartMessageAttachment(
          schoolId,
          file,
          (p) => setUploadProgress(p),
        );
        documentRef = uploaded;
      }
      await respondToSmartMessage(schoolId, {
        smartMessageId,
        optionId,
        studentId,
        responseMetadata: {
          excuseType: optionId === 'confirm' ? excuseType : null,
          reason: optionId === 'confirm' ? reason.trim() : null,
          startDate: optionId === 'confirm' ? startDate : null,
          endDate: optionId === 'confirm' ? endDate : null,
          documentRef,
          signature: optionId === 'confirm' ? signature.trim() : null,
          signedAt: optionId === 'confirm' ? new Date().toISOString() : null,
        },
      });
      if (onResponded) onResponded({ optionId });
    } catch (err) {
      console.error('[AttendanceReasonForm] submit error', err);
      setError(err?.message || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mt-3 pt-3 border-t border-base-200 space-y-3">
      <div>
        <label className="block text-xs font-bold uppercase tracking-wide text-base-content/60 mb-1.5">
          Reason
        </label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {EXCUSE_CHIPS.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setExcuseType(c.id)}
              className={`btn btn-xs ${excuseType === c.id ? 'btn-primary' : 'btn-outline'}`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <textarea
          className="textarea textarea-bordered w-full text-sm"
          rows={2}
          placeholder="Add detail (required)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-bold uppercase tracking-wide text-base-content/60">
          Dates booked off
        </label>
        <div className="flex items-center gap-2">
          <input
            type="date"
            className="input input-bordered input-sm flex-1"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <span className="text-xs text-base-content/60">to</span>
          <input
            type="date"
            className="input input-bordered input-sm flex-1"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-bold uppercase tracking-wide text-base-content/60">
          Supporting document (optional)
        </label>
        <input
          type="file"
          className="file-input file-input-bordered file-input-sm w-full"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
        {submitting && uploadProgress > 0 && uploadProgress < 100 && (
          <progress className="progress progress-primary w-full" value={uploadProgress} max="100" />
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-bold uppercase tracking-wide text-base-content/60">
          Signature
        </label>
        <input
          type="text"
          className="input input-bordered input-sm w-full"
          placeholder="Type your full name"
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
        />
        <label className="flex items-start gap-2 text-xs text-base-content/70 cursor-pointer">
          <input
            type="checkbox"
            className="checkbox checkbox-sm mt-0.5"
            checked={affirmed}
            onChange={(e) => setAffirmed(e.target.checked)}
          />
          <span>
            I confirm the information above is correct and I am the parent or guardian for this learner.
          </span>
        </label>
      </div>

      {error && <p className="text-xs text-error">{error}</p>}

      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => submit('confirm')}
          disabled={!canConfirm || submitting}
          className="btn btn-sm btn-primary"
        >
          {submitting && <span className="loading loading-spinner loading-xs" />}
          Confirm absence
        </button>
        <button
          type="button"
          onClick={() => submit('dispute')}
          disabled={submitting}
          className="btn btn-sm btn-outline"
        >
          Was present
        </button>
      </div>
    </div>
  );
}
